import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ShieldAlert, RefreshCw, CheckCircle, ChevronRight, ChevronDown, Search, Activity, ExternalLink, MessageCircle } from 'lucide-react';
import { apiClient } from '../api';
import { useChat } from '../contexts/ChatContext';

const SEVERITY_STYLES = {
  CRITICAL: { text: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/20' },
  HIGH:     { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20' },
  MEDIUM:   { text: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20' },
  LOW:      { text: 'text-emerald-400',bg: 'bg-emerald-400/10',border: 'border-emerald-400/20' },
};

const SEVERITY_BORDER = {
  CRITICAL: 'border-l-red-500',
  HIGH:     'border-l-orange-500',
  MEDIUM:   'border-l-amber-500',
  LOW:      'border-l-emerald-500',
};

const SEVERITY_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

const ECOSYSTEM_STYLES = {
  npm:      { bg: 'bg-red-500/10',    text: 'text-red-400',    border: 'border-red-500/20' },
  pip:      { bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20' },
  python:   { bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20' },
  cargo:    { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
  go:       { bg: 'bg-cyan-500/10',   text: 'text-cyan-400',   border: 'border-cyan-500/20' },
  gem:      { bg: 'bg-red-500/10',    text: 'text-red-400',    border: 'border-red-500/20' },
  maven:    { bg: 'bg-amber-500/10',  text: 'text-amber-400',  border: 'border-amber-500/20' },
  composer: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  nuget:    { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
};

const cvssBarColor = (score) => {
  if (score >= 9.0) return 'bg-red-500';
  if (score >= 7.0) return 'bg-orange-500';
  if (score >= 4.0) return 'bg-amber-500';
  return 'bg-emerald-500';
};

const cvssTextColor = (score) => cvssBarColor(score).replace('bg-', 'text-');

const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const CvssBar = ({ score }) => {
  if (score == null) return <span className="text-xs text-gray-600">N/A</span>;
  const pct = Math.min((score / 10) * 100, 100);
  return (
    <div className="flex items-center gap-2 mt-1">
      <div className="flex-1 h-1.5 bg-dark-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${cvssBarColor(score)}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-mono font-semibold ${cvssTextColor(score)}`}>
        {score.toFixed(1)} / 10
      </span>
    </div>
  );
};

const EcosystemBadge = ({ ecosystem }) => {
  if (!ecosystem) return null;
  const style = ECOSYSTEM_STYLES[ecosystem.toLowerCase()] ||
    { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' };
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded border font-mono ${style.bg} ${style.text} ${style.border}`}>
      {ecosystem}
    </span>
  );
};

const REF_LABELS = { ADVISORY: 'Advisory', FIX: 'Fix', REPORT: 'Report', WEB: 'Link' };

const Vulnerabilities = () => {
  const { openChatWithAlert } = useChat();
  const [alerts, setAlerts]           = useState([]);
  const [servers, setServers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  // Filters / sort
  const [resolvedFilter,  setResolvedFilter]  = useState('0');
  const [severityFilter,  setSeverityFilter]  = useState('all');
  const [serverFilter,    setServerFilter]    = useState('all');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [sortBy,          setSortBy]          = useState('cvss');

  // Accordion + bulk
  const [expandedId,    setExpandedId]    = useState(null);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [bulkResolving, setBulkResolving] = useState(false);

  // OSV per-CVE cache
  const fetchedOsvIds              = useRef(new Set());
  const [osvCache,     setOsvCache]     = useState({});
  const [osvLoadingIds,setOsvLoadingIds] = useState(new Set());

  // NVD daily feed
  const [nvdFeed,      setNvdFeed]      = useState(null);
  const [nvdExpanded,  setNvdExpanded]  = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (resolvedFilter !== 'all') params.set('resolved', resolvedFilter);
      if (serverFilter  !== 'all') params.set('server_id', serverFilter);
      const res = await apiClient.get(`/api/alerts/cve?${params.toString()}`);
      setAlerts(res.data.cve_alerts || []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [resolvedFilter, serverFilter]);

  const fetchServers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/servers?state=REGISTERED');
      setServers(res.data.servers || []);
    } catch {}
  }, []);

  const fetchOsvData = useCallback(async (cveId) => {
    if (fetchedOsvIds.current.has(cveId)) return;
    fetchedOsvIds.current.add(cveId);
    setOsvLoadingIds(prev => new Set([...prev, cveId]));
    try {
      const res = await apiClient.get(`/api/vulns/osv?cve_id=${encodeURIComponent(cveId)}`);
      setOsvCache(prev => ({ ...prev, [cveId]: res.data }));
    } catch {
      setOsvCache(prev => ({ ...prev, [cveId]: { found: false } }));
    } finally {
      setOsvLoadingIds(prev => { const n = new Set(prev); n.delete(cveId); return n; });
    }
  }, []);

  useEffect(() => { fetchServers(); }, [fetchServers]);
  useEffect(() => { fetchAlerts();  }, [fetchAlerts]);

  // Fetch NVD feed once on mount
  useEffect(() => {
    apiClient.get('/api/vulns/nvd-feed')
      .then(res => setNvdFeed(res.data))
      .catch(() => {});
  }, []);

  // Fetch OSV data lazily when a row is expanded
  useEffect(() => {
    if (!expandedId) return;
    const cve = alerts.find(a => a.id === expandedId);
    if (cve?.cve_id) fetchOsvData(cve.cve_id);
  }, [expandedId, alerts, fetchOsvData]);

  // ── Alert actions ──────────────────────────────────────────────────────────

  const handleResolve = async (id) => {
    setResolvingId(id);
    try {
      await apiClient.patch(`/api/alerts/cve/${id}`);
      setAlerts(prev => prev.filter(a => a.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      if (expandedId === id) setExpandedId(null);
    } catch {
      setError('Failed to resolve alert');
    } finally {
      setResolvingId(null);
    }
  };

  const handleBulkResolve = async () => {
    setBulkResolving(true);
    try {
      await Promise.all([...selectedIds].map(id => apiClient.patch(`/api/alerts/cve/${id}`)));
      setAlerts(prev => prev.filter(a => !selectedIds.has(a.id)));
      if (selectedIds.has(expandedId)) setExpandedId(null);
      setSelectedIds(new Set());
    } catch {
      setError('Failed to resolve some alerts');
    } finally {
      setBulkResolving(false);
    }
  };

  const getServerName = useCallback((serverId) => {
    const server = servers.find(s => s.id === serverId);
    return server ? server.hostname : serverId;
  }, [servers]);

  // ── Filtered / sorted list ─────────────────────────────────────────────────

  const filteredAlerts = useMemo(() => {
    let result = [...alerts];
    if (severityFilter !== 'all')
      result = result.filter(a => a.severity === severityFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(a =>
        a.cve_id?.toLowerCase().includes(q) ||
        a.package_name?.toLowerCase().includes(q)
      );
    }
    if (sortBy === 'cvss')
      result.sort((a, b) => (b.cvss_score ?? 0) - (a.cvss_score ?? 0));
    else if (sortBy === 'severity')
      result.sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 4) - (SEVERITY_ORDER[b.severity] ?? 4));
    else if (sortBy === 'asset')
      result.sort((a, b) => getServerName(a.server_id).localeCompare(getServerName(b.server_id)));
    return result;
  }, [alerts, severityFilter, searchQuery, sortBy, getServerName]);

  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL').length;
  const highCount     = alerts.filter(a => a.severity === 'HIGH').length;

  const allSelected = filteredAlerts.length > 0 && selectedIds.size === filteredAlerts.length;
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(filteredAlerts.map(a => a.id)));
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Vulnerabilities</h1>
          <p className="text-sm text-gray-500 mt-1">CVE alerts detected in installed packages across your assets</p>
        </div>
        <button onClick={fetchAlerts}
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 bg-dark-800 border border-dark-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-red-400">{criticalCount}</div>
          <div className="text-sm text-red-400/70">Critical CVEs</div>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-orange-400">{highCount}</div>
          <div className="text-sm text-orange-400/70">High CVEs</div>
        </div>
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
          <div className="text-3xl font-bold text-white">{alerts.length}</div>
          <div className="text-sm text-gray-400">Total Vulnerabilities</div>
        </div>
      </div>

      {/* NVD daily feed widget */}
      {nvdFeed && (
        <div className="bg-dark-800 border border-dark-700/50 rounded-xl overflow-hidden">
          <div
            className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-dark-700/20 transition-colors"
            onClick={() => setNvdExpanded(v => !v)}
          >
            <div className="flex items-center gap-3">
              <Activity className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-300">
                  Today's NVD Feed
                  <span className="ml-2 text-xs text-gray-500 font-normal">{nvdFeed.date}</span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {nvdFeed.total_today} CVEs published today
                  {nvdFeed.high_critical.length > 0 && (
                    <span className="ml-1 text-orange-400 font-semibold">
                      · {nvdFeed.high_critical.length} HIGH/CRITICAL
                    </span>
                  )}
                </p>
              </div>
            </div>
            {nvdExpanded
              ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
              : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
          </div>

          {nvdExpanded && (
            <div className="border-t border-dark-700/50">
              {nvdFeed.high_critical.length === 0 ? (
                <p className="text-center py-6 text-sm text-gray-500">No HIGH or CRITICAL CVEs published today.</p>
              ) : (
                <div className="divide-y divide-dark-700/30 max-h-72 overflow-y-auto">
                  {nvdFeed.high_critical.map(item => {
                    const sev = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.LOW;
                    return (
                      <div key={item.cve_id} className="flex items-start gap-3 px-5 py-3 hover:bg-dark-700/20 transition-colors">
                        <span className={`mt-0.5 flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${sev.bg} ${sev.text} border ${sev.border}`}>
                          {item.severity}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://nvd.nist.gov/vuln/detail/${item.cve_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="text-sm font-mono text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                            >
                              {item.cve_id}
                            </a>
                            {item.cwe?.length > 0 && (
                              <span className="text-xs text-gray-600 font-mono">{item.cwe[0]}</span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-right">
                          {item.cvss_score != null && (
                            <span className={`text-xs font-mono font-semibold ${cvssTextColor(item.cvss_score)}`}>
                              {item.cvss_score.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by CVE ID or package name…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            {['all', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => {
              const style = s === 'all' ? null : SEVERITY_STYLES[s];
              const active = severityFilter === s;
              return (
                <button key={s} onClick={() => setSeverityFilter(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    active
                      ? s === 'all'
                        ? 'bg-orange-500/20 text-orange-400 border-orange-500/40'
                        : `${style.bg} ${style.text} ${style.border}`
                      : 'bg-dark-800 text-gray-500 border-dark-700 hover:text-gray-300 hover:border-gray-600'
                  }`}>
                  {s === 'all' ? 'ALL' : s}
                </button>
              );
            })}
          </div>
          <div className="w-px h-4 bg-dark-700" />
          <select value={resolvedFilter} onChange={e => setResolvedFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30">
            <option value="0">Unresolved</option>
            <option value="1">Resolved</option>
            <option value="all">All</option>
          </select>
          <select value={serverFilter} onChange={e => setServerFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30">
            <option value="all">All Assets</option>
            {servers.map(s => <option key={s.id} value={s.id}>{s.hostname}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30">
            <option value="cvss">Highest CVSS</option>
            <option value="severity">By Severity</option>
            <option value="asset">By Asset</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
          <strong>Error:</strong> {error}
          <button onClick={fetchAlerts} className="ml-4 text-sm underline hover:text-red-300">Retry</button>
        </div>
      )}

      {/* Alert list */}
      <div className="bg-dark-800 border border-dark-700/50 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-700/50">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
              title="Select all visible"
              className="w-4 h-4 rounded border-gray-600 bg-dark-700 accent-orange-500 cursor-pointer flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-300">CVE Alerts</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
                {alerts.length !== filteredAlerts.length ? ` (filtered from ${alerts.length})` : ' shown'}
              </p>
            </div>
          </div>
          {selectedIds.size > 0 && (
            <button onClick={handleBulkResolve} disabled={bulkResolving}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-lg hover:bg-emerald-400/20 transition-colors disabled:opacity-50">
              {bulkResolving
                ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Resolving…</>
                : <><CheckCircle className="w-3.5 h-3.5" /> Resolve selected ({selectedIds.size})</>}
            </button>
          )}
        </div>

        <div className="max-h-[600px] overflow-y-auto overflow-x-hidden divide-y divide-dark-700/30">
          {loading ? (
            <div className="text-center py-10 text-gray-500 text-sm">Loading vulnerability alerts…</div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              {searchQuery || severityFilter !== 'all'
                ? 'No alerts match your filters.'
                : 'No CVE alerts yet. Alerts are generated when the agent sends a snapshot and the CVE scan job runs.'}
            </div>
          ) : (
            filteredAlerts.map(cve => {
              const sev        = SEVERITY_STYLES[cve.severity] || SEVERITY_STYLES.LOW;
              const leftBorder = SEVERITY_BORDER[cve.severity] || 'border-l-gray-700';
              const isExpanded = expandedId === cve.id;
              const isSelected = selectedIds.has(cve.id);
              const serverName = getServerName(cve.server_id);
              const osv        = osvCache[cve.cve_id];
              const osvLoading = osvLoadingIds.has(cve.cve_id);

              return (
                <div key={cve.id} className={`border-l-2 ${leftBorder} ${isSelected ? 'bg-dark-700/20' : ''}`}>

                  {/* ── Collapsed row ── */}
                  <div
                    className="flex items-center gap-2 sm:gap-3 px-4 py-3 hover:bg-dark-700/30 transition-colors cursor-pointer select-none"
                    onClick={() => setExpandedId(isExpanded ? null : cve.id)}
                  >
                    <input type="checkbox" checked={isSelected}
                      onChange={e => toggleSelect(cve.id, e)}
                      onClick={e => e.stopPropagation()}
                      className="w-4 h-4 rounded border-gray-600 bg-dark-700 accent-orange-500 cursor-pointer flex-shrink-0" />
                    <span className="text-gray-500 flex-shrink-0">
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </span>
                    <span className="text-xs font-mono text-gray-500 w-12 flex-shrink-0">
                      #{String(cve.id).padStart(3, '0')}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${sev.bg} ${sev.text} border ${sev.border}`}>
                        {cve.severity}
                      </span>
                      <EcosystemBadge ecosystem={cve.ecosystem} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-mono text-gray-200 truncate block">{cve.cve_id}</span>
                      <span className="text-xs text-gray-500 truncate block">{cve.package_name}@{cve.version}</span>
                    </div>
                    {/* Fix available indicator */}
                    {osv?.fix_version && (
                      <span className="hidden sm:inline text-xs text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap">
                        ↑ {osv.fix_version}
                      </span>
                    )}
                    <span className="hidden sm:block text-xs font-mono text-gray-400 flex-shrink-0 w-16 text-right">
                      CVSS {cve.cvss_score != null ? cve.cvss_score.toFixed(1) : 'N/A'}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0 hidden md:block w-32 truncate" title={serverName}>
                      {serverName}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0 w-16 text-right"
                      title={cve.detected_at ? new Date(cve.detected_at).toLocaleString() : ''}>
                      {relativeTime(cve.detected_at)}
                    </span>
                    <div className="flex-shrink-0 ml-1" onClick={e => e.stopPropagation()}>
                      {cve.resolved ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">
                          Resolved
                        </span>
                      ) : (
                        <button onClick={() => handleResolve(cve.id)} disabled={resolvingId === cve.id}
                          className="text-gray-500 hover:text-emerald-400 transition-colors disabled:opacity-50" title="Mark as resolved">
                          {resolvingId === cve.id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <CheckCircle className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── Expanded panel ── */}
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 border-t border-dark-700/40 bg-dark-900/40">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 pt-3">

                        {/* Vulnerability */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Vulnerability</p>
                          <p className="text-sm font-mono text-gray-100">{cve.cve_id}</p>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${sev.bg} ${sev.text} border ${sev.border}`}>
                            {cve.severity}
                          </span>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">CVSS Score</p>
                            <CvssBar score={cve.cvss_score} />
                          </div>
                        </div>

                        {/* Package + OSV fix */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Package & Fix</p>
                          <p className="text-sm font-mono text-gray-100">{cve.package_name}</p>
                          <p className="text-xs text-gray-500">
                            Installed: <span className="font-mono text-gray-300">{cve.version}</span>
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Ecosystem:</span>
                            <EcosystemBadge ecosystem={cve.ecosystem} />
                          </div>

                          {/* OSV fix data */}
                          {osvLoading && (
                            <p className="text-xs text-gray-600 flex items-center gap-1.5 mt-2">
                              <RefreshCw className="w-3 h-3 animate-spin" /> Looking up OSV…
                            </p>
                          )}
                          {osv && !osvLoading && (
                            <div className="mt-2 space-y-1">
                              {osv.fix_version ? (
                                <p className="text-xs text-emerald-400 font-semibold">
                                  ↑ Fix available: <span className="font-mono">{osv.fix_version}</span>
                                </p>
                              ) : osv.found === false ? (
                                <p className="text-xs text-gray-600">No OSV entry found</p>
                              ) : (
                                <p className="text-xs text-gray-500">No fix version available yet</p>
                              )}
                              {osv.affected_range && (
                                <p className="text-xs text-gray-500">
                                  Affects: <span className="font-mono text-gray-400">{osv.affected_range}</span>
                                </p>
                              )}
                              {osv.osv_id && osv.osv_id !== cve.cve_id && (
                                <p className="text-xs text-gray-600">
                                  OSV ID: <span className="font-mono">{osv.osv_id}</span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</p>
                          {cve.description
                            ? <p className="text-xs text-gray-400 leading-relaxed">{cve.description}</p>
                            : <p className="text-xs text-gray-600">No description available</p>}
                        </div>

                        {/* Asset + timeline + references */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Asset & Timeline</p>
                          <p className="text-xs text-gray-500">
                            Asset: <span className="font-mono text-gray-300">{serverName}</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Detected: <span className="text-gray-300">
                              {cve.detected_at ? new Date(cve.detected_at).toLocaleString() : '—'}
                            </span>
                          </p>
                          {cve.resolved_at && (
                            <p className="text-xs text-gray-500">
                              Resolved: <span className="text-emerald-400">{new Date(cve.resolved_at).toLocaleString()}</span>
                            </p>
                          )}

                          {/* OSV references */}
                          {osv?.references?.length > 0 && (
                            <div className="pt-1">
                              <p className="text-xs text-gray-600 mb-1.5">References</p>
                              <div className="flex flex-wrap gap-1.5">
                                {osv.references.map((ref, i) => (
                                  <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-dark-700/50 border border-dark-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                                    {REF_LABELS[ref.type] || ref.type}
                                    <ExternalLink className="w-2.5 h-2.5" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {!cve.resolved && (
                            <button onClick={() => handleResolve(cve.id)} disabled={resolvingId === cve.id}
                              className="mt-1 flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-lg hover:bg-emerald-400/20 transition-colors disabled:opacity-50">
                              {resolvingId === cve.id
                                ? <><RefreshCw className="w-3 h-3 animate-spin" /> Resolving…</>
                                : <><CheckCircle className="w-3 h-3" /> Mark as Resolved</>}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); openChatWithAlert([cve.id], 'cve'); }}
                            className="mt-1 flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors"
                          >
                            <MessageCircle className="w-3 h-3" /> Discuss with AI
                          </button>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Vulnerabilities;
